import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle2, Clock, TrendingUp, Eye, RefreshCw, ExternalLink, ChevronDown, ChevronUp, Target, Zap, Activity, X, Link2, Search, EyeOff, RotateCcw, MinusCircle, Download, TrendingDown, ChevronLeft, ChevronRight, Calendar, Trash2 } from 'lucide-react';

const IGNORE_REASONS = [
  'Links already sent',
  'Unable to fix',
  'Needs dev team',
  'False positive',
] as const;

// Score Ring SVG component — Siteimprove-style circular score indicator
const ScoreRing: React.FC<{
  score: number;
  previousScore?: number | null;
  label: string;
  color: string;
  size?: number;
}> = ({ score, previousScore, label, color, size = 140 }) => {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(100, score));
  const offset = circumference - (progress / 100) * circumference;
  const delta = previousScore != null ? score - previousScore : null;

  const getGrade = (s: number) => {
    if (s >= 90) return 'A';
    if (s >= 80) return 'B';
    if (s >= 70) return 'C';
    if (s >= 60) return 'D';
    return 'F';
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-black text-slate-800">{score}</span>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{getGrade(score)}</span>
        </div>
      </div>
      <span className="text-xs font-bold text-slate-600 text-center">{label}</span>
      {delta !== null && delta !== 0 && (
        <span className={`text-[10px] font-bold flex items-center gap-0.5 ${delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
          {delta > 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
          {delta > 0 ? '+' : ''}{delta}
        </span>
      )}
    </div>
  );
};
import { toast } from '../components/Toast';
import { Environment } from '../types';
import EmptyState from '../components/EmptyState';

interface AuditHistoryEntry {
  id: string;
  pages_audited: number;
  total_issues: number;
  critical_issues: number;
  high_issues: number;
  medium_issues: number;
  low_issues: number;
  new_pages: number;
  trigger_type: string;
  started_at: number;
  completed_at: number;
}

interface SEOMonitorProps {
  activeEnv: Environment | null;
}

interface SEOIssue {
  id: string;
  url: string;
  issue_type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  title: string;
  description: string;
  recommendation: string;
  status: string;
  ignore_reason?: string;
  ignored_at?: number;
  detected_at: number;
}

interface SEOChange {
  id: string;
  url: string;
  change_type: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  detected_at: number;
}

interface SEOTask {
  id: string;
  title: string;
  description: string;
  priority: string;
  impact: string;
  effort: string;
  status: string;
  created_at: number;
}

const SEOMonitor: React.FC<SEOMonitorProps> = ({ activeEnv }) => {
  const [loading, setLoading] = useState(true);
  const [auditing, setAuditing] = useState(false);
  const [auditProgress, setAuditProgress] = useState({ current: 0, total: 0 });
  const [monitoring, setMonitoring] = useState<any>(null);
  const [issues, setIssues] = useState<SEOIssue[]>([]);
  const [changes, setChanges] = useState<SEOChange[]>([]);
  const [tasks, setTasks] = useState<SEOTask[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [auditUrls, setAuditUrls] = useState('');
  const [showAuditPanel, setShowAuditPanel] = useState(false);
  const [backgroundAudit, setBackgroundAudit] = useState(false);
  const [showFixModal, setShowFixModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<SEOIssue | null>(null);
  const [generatedFix, setGeneratedFix] = useState<string>('');
  const [existingPageData, setExistingPageData] = useState<any>(null);
  const [optimizedContent, setOptimizedContent] = useState<string>('');
  const [optimizing, setOptimizing] = useState(false);
  const [sortBy, setSortBy] = useState<string>('severity');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [auditHistory, setAuditHistory] = useState<AuditHistoryEntry[]>([]);
  const [newPageUrls, setNewPageUrls] = useState<Set<string>>(new Set());
  const [suggestedSlug, setSuggestedSlug] = useState<string>('');
  const [autoAuditRunning, setAutoAuditRunning] = useState(false);
  const [historyDateFilter, setHistoryDateFilter] = useState<string>('all');
  const [historyTypeFilter, setHistoryTypeFilter] = useState<string>('all');
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_PAGE_SIZE = 25;
  const [ignoredIssues, setIgnoredIssues] = useState<SEOIssue[]>([]);
  const [dismissedIssues, setDismissedIssues] = useState<SEOIssue[]>([]);
  const [recurringIssues, setRecurringIssues] = useState<SEOIssue[]>([]);
  const [issueTab, setIssueTab] = useState<'active' | 'dismissed' | 'recurring' | 'ignored'>('active');
  const [scores, setScores] = useState<{ seo: number; qa: number; accessibility: number; overall: number } | null>(null);
  const [previousScores, setPreviousScores] = useState<{ seo: number; qa: number; accessibility: number; overall: number } | null>(null);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [ignoreIssue, setIgnoreIssue] = useState<SEOIssue | null>(null);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<string>>(new Set());
  const [bulkIgnoreMode, setBulkIgnoreMode] = useState(false);
  const [showFixedPanel, setShowFixedPanel] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState({ issues: true, changes: true, history: true });
  const toggleSection = (key: keyof typeof sectionsOpen) =>
    setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  // New page meta generator
  const [showMetaGenPanel, setShowMetaGenPanel] = useState(false);
  const [metaGenTitle, setMetaGenTitle] = useState('');
  const [metaGenContent, setMetaGenContent] = useState('');
  const [metaGenUrl, setMetaGenUrl] = useState('');
  const [metaGenType, setMetaGenType] = useState<'missing_meta_description' | 'missing_meta_title' | 'missing_h1' | 'url_slug'>('missing_meta_description');
  const [metaGenResult, setMetaGenResult] = useState('');
  const [metaGenSlug, setMetaGenSlug] = useState('');
  const [metaGenGenerating, setMetaGenGenerating] = useState(false);

  // Siteimprove integration state
  const [siConfigured, setSiConfigured] = useState(false);
  const [siScores, setSiScores] = useState<{ overall: number; seo: number; qa: number; accessibility: number } | null>(null);
  const [siSummary, setSiSummary] = useState<any>(null);
  const [siIssues, setSiIssues] = useState<any[]>([]);
  const [siLoading, setSiLoading] = useState(false);

  // Normalize URL for stable localStorage key comparison (strips trailing slash, lowercases)
  const normalizeKey = (url: string, issueType: string) =>
    `${url.trim().toLowerCase().replace(/\/$/, '')}::${issueType}`;

  // Per-environment localStorage key helpers
  const ignoredStorageKey = () => activeEnv ? `lle_ignored_keys_${activeEnv.id}` : 'lle_ignored_keys';
  const dismissedStorageKey = () => activeEnv ? `lle_dismissed_keys_${activeEnv.id}` : 'lle_dismissed_keys';

  useEffect(() => {
    if (!activeEnv || activeEnv.isSample) return;

    loadMonitoringData(true); // Initial load with auto-audit check
  }, [activeEnv?.id]);

  // Check if Siteimprove is configured on mount
  useEffect(() => {
    const checkSi = async () => {
      try {
        const resp = await fetch('/api/siteimprove?action=check', { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          setSiConfigured(data.configured === true);
        }
      } catch { /* not configured */ }
    };
    checkSi();
  }, []);

  const fetchSiteimproveData = async () => {
    setSiLoading(true);
    try {
      const [summaryResp, issuesResp] = await Promise.all([
        fetch('/api/siteimprove?action=summary', { credentials: 'include' }),
        fetch('/api/siteimprove?action=issues', { credentials: 'include' })
      ]);

      if (summaryResp.ok) {
        const data = await summaryResp.json();
        if (data.success) {
          if (data.scores) setSiScores(data.scores);
          setSiSummary({
            seo: data.seo || null,
            qa: data.qa || null,
            accessibility: data.accessibility || null,
            totalPages: data.totalPages || 0
          });
        } else {
          toast.error(data.error || 'Failed to fetch Siteimprove data');
        }
      }

      if (issuesResp.ok) {
        const data = await issuesResp.json();
        if (data.success && data.issues) setSiIssues(data.issues);
      }
    } catch (error) {
      console.error('Siteimprove fetch error:', error);
      toast.error('Failed to connect to Siteimprove');
    } finally {
      setSiLoading(false);
    }
  };

  const loadMonitoringData = async (checkAutoAudit = false) => {
    if (!activeEnv) return;

    try {
      const response = await fetch(`/api/seo/monitor?environmentId=${activeEnv.id}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMonitoring(data.monitoring);

        // localStorage tracks user actions per-environment.
        // Use env-scoped keys so actions in one environment don't affect another.
        const envIgnoredKey = `lle_ignored_keys_${activeEnv.id}`;
        const envDismissedKey = `lle_dismissed_keys_${activeEnv.id}`;

        // Migrate old global keys to per-env on first load (one-time)
        const globalIgnored = localStorage.getItem('lle_ignored_keys');
        if (globalIgnored && !localStorage.getItem(envIgnoredKey)) {
          localStorage.setItem(envIgnoredKey, globalIgnored);
        }
        const globalDismissed = localStorage.getItem('lle_dismissed_keys');
        if (globalDismissed && !localStorage.getItem(envDismissedKey)) {
          localStorage.setItem(envDismissedKey, globalDismissed);
        }

        const rawIgnoredKeys: string[] = JSON.parse(localStorage.getItem(envIgnoredKey) || '[]');
        const rawDismissedKeys: string[] = JSON.parse(localStorage.getItem(envDismissedKey) || '[]');
        const localIgnoredKeys = new Set<string>(rawIgnoredKeys.map(k => k.toLowerCase().replace(/\/$/, '')));
        const localDismissedKeys = new Set<string>(rawDismissedKeys.map(k => k.toLowerCase().replace(/\/$/, '')));

        // Do NOT merge server ignored/dismissed into local sets — the server already
        // excludes ignored types from open issues in the POST handler. Merging causes
        // false filtering when keys accumulate over time.

        const recurringKeys = new Set<string>(
          (data.recurringIssues || []).map((i: SEOIssue) => normalizeKey(i.url, i.issue_type))
        );

        // Classify incoming active issues
        const newRecurring: SEOIssue[] = [];
        const activeOnly = (data.issues || []).filter((i: SEOIssue) => {
          const key = normalizeKey(i.url, i.issue_type);
          if (localIgnoredKeys.has(key)) return false;   // ignored — never show in active
          if (recurringKeys.has(key)) return false;       // already recurring
          if (localDismissedKeys.has(key)) {
            // Was dismissed but reappeared — promote to recurring
            newRecurring.push({ ...i, status: 'recurring' });
            return false;
          }
          return true;
        });

        const mergedRecurring = [
          ...(data.recurringIssues || []),
          ...newRecurring.filter(n => !recurringKeys.has(normalizeKey(n.url, n.issue_type)))
        ];

        // Deduplicate ignored/dismissed lists by url+issue_type
        const seenIgnored = new Set<string>();
        const dedupIgnored = (data.ignoredIssues || []).filter((i: SEOIssue) => {
          const k = `${i.url}::${i.issue_type}`;
          if (seenIgnored.has(k)) return false;
          seenIgnored.add(k);
          return true;
        });
        // Merge server dismissed issues with locally persisted objects.
        // localStorage is authoritative — dismissed issues stay until manually cleared.
        const localDismissedObjects: SEOIssue[] = JSON.parse(localStorage.getItem(`dismissed_objects_${activeEnv.id}`) || '[]');
        const seenDismissed = new Set<string>();
        const allDismissedSources = [...localDismissedObjects, ...(data.dismissedIssues || [])];
        const dedupDismissed = allDismissedSources.filter((i: SEOIssue) => {
          const k = normalizeKey(i.url, i.issue_type);
          if (seenDismissed.has(k)) return false;
          seenDismissed.add(k);
          return true;
        });

        setIssues(activeOnly);
        setIgnoredIssues(dedupIgnored);
        setDismissedIssues(dedupDismissed);
        setRecurringIssues(mergedRecurring);
        setChanges(data.changes || []);
        setTasks(data.tasks || []);
        setAuditHistory(data.auditHistory || []);
        if (data.newPages) {
          setNewPageUrls(new Set(data.newPages.map((p: any) => p.url)));
        }
        if (data.scores) setScores(data.scores);
        if (data.previousScores) setPreviousScores(data.previousScores);

        // Auto-audit: only trigger at 5am EST (10am UTC / 9am UTC during EDT)
        // Check once per calendar day using localStorage
        // Only check on initial page load, not on every data refresh — prevents
        // background audits from resetting dismissed issues mid-session.
        if (checkAutoAudit && !autoAuditRunning && data.monitoring?.pagesMonitored > 0) {
          const nowUtc = new Date();
          const utcHour = nowUtc.getUTCHours();
          const is5amEST = utcHour === 10 || utcHour === 9; // 10=EST, 9=EDT
          const todayStr = nowUtc.toISOString().split('T')[0];
          const lastAutoDay = localStorage.getItem('lle_last_auto_audit_day');
          if (is5amEST && lastAutoDay !== todayStr) {
            localStorage.setItem('lle_last_auto_audit_day', todayStr);
            triggerAutoAudit();
          }
        }
      }
    } catch (error) {
      console.error('Failed to load monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerAutoAudit = async () => {
    if (!activeEnv || autoAuditRunning) return;
    setAutoAuditRunning(true);
    try {
      // Load user-configured sitemaps from Settings (API first, localStorage fallback)
      let sitemaps: string[] = [];
      try {
        const smResp = await fetch(`/api/sitemaps?environmentId=${activeEnv.id}`, { credentials: 'include' });
        if (smResp.ok) {
          const smData = await smResp.json();
          sitemaps = smData.sitemaps || [];
        }
      } catch (e) { /* ignore */ }
      if (sitemaps.length === 0) {
        const stored = localStorage.getItem(`sitemaps_${activeEnv.id}`);
        if (stored) sitemaps = JSON.parse(stored);
      }
      if (sitemaps.length === 0) {
        toast.warning('No sitemaps configured. Add your sitemap URLs in Settings.');
        return;
      }
      const allUrls: string[] = [];
      for (const sitemapUrl of sitemaps) {
        try {
          const resp = await fetch(`/api/seo/sitemap?url=${encodeURIComponent(sitemapUrl)}`, {
            credentials: 'include'
          });
          if (resp.ok) {
            const data = await resp.json();
            allUrls.push(...data.urls);
          }
        } catch (e) { /* skip failed sitemaps */ }
      }

      if (allUrls.length === 0) return;

      const urls = allUrls.slice(0, 500);
      toast.info(`Auto-audit started: checking ${urls.length} pages...`);

      const batchSize = 20;
      for (let i = 0; i < urls.length; i += batchSize) {
        const batch = urls.slice(i, i + batchSize);
        await fetch('/api/seo/monitor', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            environmentId: activeEnv.id,
            urls: batch,
            triggerType: 'auto'
          })
        });
        if (i + batchSize < urls.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      toast.success(`Daily auto-audit complete! ${urls.length} pages checked`);
      await loadMonitoringData();
    } catch (error) {
      console.error('Auto-audit failed:', error);
    } finally {
      setAutoAuditRunning(false);
    }
  };

  const generateMetaTagFix = (issue: SEOIssue): string => {
    const url = new URL(issue.url);
    const pageName = url.pathname.split('/').filter(Boolean).pop() || 'Home';
    const formattedName = pageName.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');

    switch (issue.issue_type) {
      case 'missing_meta_title':
        return `<title>${formattedName} | Laboratory for Laser Energetics</title>

<!-- Add this inside the <head> section of your HTML -->
<!-- Recommended length: 50-60 characters -->
<!-- Current length: ${(`${formattedName} | Laboratory for Laser Energetics`).length} characters -->`;

      case 'missing_meta_description':
        const description = `Learn about ${formattedName.toLowerCase()} at the Laboratory for Laser Energetics. Leading research in high-energy-density physics, inertial confinement fusion, and laser technology.`;
        return `<meta name="description" content="${description}">

<!-- Add this inside the <head> section of your HTML -->
<!-- Recommended length: 150-160 characters -->
<!-- Current length: ${description.length} characters -->`;

      case 'missing_h1':
        return `<h1>${formattedName}</h1>

<!-- Add this as the first major heading on your page -->
<!-- Location: Near the top of the <body> section, usually within the main content area -->
<!-- Best practice: Only one H1 per page -->`;

      case 'missing_canonical':
        return `<link rel="canonical" href="${issue.url}">

<!-- Add this inside the <head> section of your HTML -->
<!-- This tells search engines this is the preferred version of this page -->`;

      default:
        return `<!-- Fix for: ${issue.title} -->

${issue.recommendation}

<!-- Page URL: ${issue.url} -->
<!-- Issue Type: ${issue.issue_type} -->`;
    }
  };

  const handleFixNowClick = async (issue: SEOIssue) => {
    setSelectedIssue(issue);
    setShowFixModal(true);
    setExistingPageData(null);
    setOptimizedContent('');
    setGeneratedFix('');
    setOptimizing(true);

    // Fetch the live page to get existing meta tags and content
    try {
      const response = await fetch(`/api/seo/fetch-page?url=${encodeURIComponent(issue.url)}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch page data');
      }

      const data = await response.json();
      setExistingPageData(data);

      // Call optimization API
      await optimizeContent(issue, data);
    } catch (error: any) {
      console.error('Failed in handleFixNowClick:', error);
      setOptimizing(false);
      toast.error(`Failed to analyze page: ${error.message}. Please try again.`);
      setShowFixModal(false);
    }
  };

  const optimizeContent = async (issue: SEOIssue, pageData: any) => {
    try {
      console.log('Starting optimization for:', issue.url);
      console.log('Page data:', {
        hasTitle: !!pageData.title,
        hasContent: !!pageData.textContent,
        contentLength: pageData.textContent?.length
      });

      const response = await fetch('/api/seo/optimize', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: issue.url,
          issueType: issue.issue_type,
          pageTitle: pageData.title,
          pageContent: pageData.textContent,
          existingMeta: pageData.metaDescription,
          existingH1: pageData.h1
        })
      });

      console.log('Optimize API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Optimize API error:', response.status, errorData);
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Optimized content received:', data.optimized?.substring(0, 100));

      if (!data.optimized) {
        throw new Error('API returned empty response');
      }

      setOptimizedContent(data.optimized);
      setGeneratedFix(data.optimized);
      if (data.suggestedSlug) setSuggestedSlug(data.suggestedSlug);
      setOptimizing(false);
    } catch (error: any) {
      console.error('Failed to optimize:', error);
      setOptimizing(false);
      throw error; // Re-throw to be caught by handleFixNowClick
    }
  };

  const IGNORE_REASONS = [
    'Links already sent',
    'Unable to fix',
    'Needs dev team',
    'False positive',
    'Other'
  ];

  const handleOpenIgnoreModal = (issue: SEOIssue) => {
    setIgnoreIssue(issue);
    setIgnoreReason('');
    setBulkIgnoreMode(false);
    setShowIgnoreModal(true);
  };

  const handleIgnoreIssue = async () => {
    if (!ignoreIssue || !activeEnv) return;

    const issueToIgnore = ignoreIssue;
    const reason = ignoreReason.trim();

    // Optimistic update — move immediately, API call in background
    const ignoredItem = {
      ...issueToIgnore,
      status: 'ignored',
      ignore_reason: reason,
      ignored_at: Math.floor(Date.now() / 1000)
    };
    // Persist to localStorage so it survives data refreshes (normalized key)
    const ignoreKey = normalizeKey(issueToIgnore.url, issueToIgnore.issue_type);
    const storedIgnored: string[] = JSON.parse(localStorage.getItem(ignoredStorageKey()) || '[]');
    if (!storedIgnored.includes(ignoreKey)) {
      localStorage.setItem(ignoredStorageKey(), JSON.stringify([...storedIgnored, ignoreKey]));
    }

    setIgnoredIssues(prev => {
      // Deduplicate before adding
      const existing = new Set(prev.map(i => `${i.url}::${i.issue_type}`));
      if (existing.has(ignoreKey)) return prev;
      return [ignoredItem, ...prev];
    });
    // Remove from whichever list it came from (active or recurring)
    setIssues(prev => prev.filter(i => i.id !== issueToIgnore.id));
    setRecurringIssues(prev => prev.filter(i => i.id !== issueToIgnore.id));
    setShowIgnoreModal(false);
    setShowFixModal(false);
    setIgnoreIssue(null);
    setIgnoreReason('');
    toast.info('Moving issue to Ignored...');

    try {
      const response = await fetch('/api/seo/monitor', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environmentId: activeEnv.id,
          issueId: issueToIgnore.id,
          action: 'ignore',
          reason
        })
      });

      if (response.ok) {
        toast.success('Issue ignored. It will not reappear on future audits.');
      } else {
        toast.warning('Issue moved locally but may not have saved to server.');
      }
    } catch (error: any) {
      console.error('Failed to ignore issue:', error);
      toast.warning('Issue moved locally but server update failed.');
    }
  };

  const handleDismissIssue = async (issue: SEOIssue, source: 'active' | 'recurring' = 'active') => {
    if (!activeEnv) return;

    const dismissedItem = { ...issue, status: 'dismissed', ignored_at: Math.floor(Date.now() / 1000) };

    // Persist key to localStorage so refreshes don't bring it back
    const dismissKey = normalizeKey(issue.url, issue.issue_type);
    const storedDismissed: string[] = JSON.parse(localStorage.getItem(dismissedStorageKey()) || '[]');
    if (!storedDismissed.includes(dismissKey)) {
      localStorage.setItem(dismissedStorageKey(), JSON.stringify([...storedDismissed, dismissKey]));
    }

    // Also persist the full issue object so details survive reloads/audits
    const storedObjects: SEOIssue[] = JSON.parse(localStorage.getItem(`dismissed_objects_${activeEnv.id}`) || '[]');
    const existingKeys = new Set(storedObjects.map(i => normalizeKey(i.url, i.issue_type)));
    if (!existingKeys.has(dismissKey)) {
      localStorage.setItem(`dismissed_objects_${activeEnv.id}`, JSON.stringify([dismissedItem, ...storedObjects]));
    }

    setDismissedIssues(prev => {
      const existing = new Set(prev.map(i => `${i.url}::${i.issue_type}`));
      if (existing.has(dismissKey)) return prev;
      return [dismissedItem, ...prev];
    });
    if (source === 'recurring') {
      setRecurringIssues(prev => prev.filter(i => i.id !== issue.id));
      toast.success('Issue dismissed. If it reappears it will return to Recurring.');
    } else {
      setIssues(prev => prev.filter(i => i.id !== issue.id));
      toast.success('Issue dismissed. If it reappears it will move to Recurring.');
    }

    try {
      await fetch('/api/seo/monitor', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environmentId: activeEnv.id,
          issueId: issue.id,
          action: 'dismiss'
        })
      });
    } catch (error) {
      console.error('Failed to dismiss issue:', error);
    }
  };

  const handleClearDismissed = () => {
    if (!activeEnv) return;
    if (!confirm('Clear all dismissed/fixed issues? This cannot be undone.')) return;

    // Clear localStorage keys and objects
    localStorage.removeItem(`dismissed_objects_${activeEnv.id}`);
    const storedKeys: string[] = JSON.parse(localStorage.getItem(dismissedStorageKey()) || '[]');
    // Only remove keys belonging to current dismissed issues
    const dismissedKeys = new Set(dismissedIssues.map(i => normalizeKey(i.url, i.issue_type)));
    const remaining = storedKeys.filter(k => !dismissedKeys.has(k.toLowerCase().replace(/\/$/, '')));
    localStorage.setItem(dismissedStorageKey(), JSON.stringify(remaining));

    setDismissedIssues([]);
    toast.success('Dismissed issues cleared.');
  };

  const handleReactivateFromDismissed = async (issue: SEOIssue) => {
    if (!activeEnv) return;

    // Remove from localStorage keys and objects
    const reactivateKey = normalizeKey(issue.url, issue.issue_type);
    const storedDismissed: string[] = JSON.parse(localStorage.getItem(dismissedStorageKey()) || '[]');
    localStorage.setItem(
      dismissedStorageKey(),
      JSON.stringify(storedDismissed.filter(k =>
        k !== reactivateKey && k !== `${issue.url}::${issue.issue_type}`
      ))
    );
    const storedObjects: SEOIssue[] = JSON.parse(localStorage.getItem(`dismissed_objects_${activeEnv.id}`) || '[]');
    localStorage.setItem(
      `dismissed_objects_${activeEnv.id}`,
      JSON.stringify(storedObjects.filter(i => normalizeKey(i.url, i.issue_type) !== reactivateKey))
    );

    const reactivated = { ...issue, status: 'open', ignore_reason: undefined, ignored_at: undefined };
    setIssues(prev => [reactivated, ...prev]);
    setDismissedIssues(prev => prev.filter(i => i.id !== issue.id));
    toast.success('Issue moved back to Active.');
    try {
      await fetch('/api/seo/monitor', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environmentId: activeEnv.id, issueId: issue.id, action: 'reactivate' })
      });
    } catch (error) {
      console.error('Failed to reactivate:', error);
    }
  };

  const handleReactivateIssue = async (issue: SEOIssue) => {
    if (!activeEnv) return;

    // Remove from localStorage so future audits can re-detect it (normalized + raw for compat)
    const reactivateKey = normalizeKey(issue.url, issue.issue_type);
    const rawKey = `${issue.url}::${issue.issue_type}`;
    const storedIgnored: string[] = JSON.parse(localStorage.getItem(ignoredStorageKey()) || '[]');
    localStorage.setItem(ignoredStorageKey(), JSON.stringify(storedIgnored.filter(k => k !== reactivateKey && k !== rawKey)));
    const storedDismissed: string[] = JSON.parse(localStorage.getItem(dismissedStorageKey()) || '[]');
    localStorage.setItem(dismissedStorageKey(), JSON.stringify(storedDismissed.filter(k => k !== reactivateKey && k !== rawKey)));

    // Optimistic update — move from Ignored → Active immediately
    const reactivated = { ...issue, status: 'open', ignore_reason: undefined, ignored_at: undefined };
    setIssues(prev => [reactivated, ...prev]);
    setIgnoredIssues(prev => prev.filter(i => i.id !== issue.id));
    toast.success('Issue moved to Active. It will be checked on the next audit.');

    try {
      await fetch('/api/seo/monitor', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environmentId: activeEnv.id,
          issueId: issue.id,
          action: 'reactivate'
        })
      });
    } catch (error) {
      console.error('Failed to reactivate issue:', error);
      toast.warning('Moved locally but server update failed.');
    }
  };

  const handleOptimize = async () => {
    if (!selectedIssue || !existingPageData) return;
    await optimizeContent(selectedIssue, existingPageData);
  };

  const handleGenerateNewPageMeta = async () => {
    if (!metaGenTitle.trim() && !metaGenContent.trim()) return;
    setMetaGenGenerating(true);
    setMetaGenResult('');
    setMetaGenSlug('');
    try {
      const response = await fetch('/api/seo/optimize', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: metaGenUrl.trim() || 'https://example.com/new-page',
          issueType: metaGenType,
          pageTitle: metaGenTitle,
          pageContent: metaGenContent,
          existingMeta: '',
          existingH1: ''
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const data = await response.json();
      if (!data.optimized) throw new Error('API returned empty response');
      setMetaGenResult(data.optimized);
      if (data.suggestedSlug) setMetaGenSlug(data.suggestedSlug);
    } catch (error: any) {
      toast.error(`Failed to generate: ${error.message}`);
    } finally {
      setMetaGenGenerating(false);
    }
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(generatedFix);
    toast.success('Copied to clipboard!');
  };

  const getFieldLabel = (issueType: string): string => {
    if (issueType.includes('title')) return 'META TITLE';
    if (issueType.includes('description')) return 'META DESCRIPTION';
    if (issueType.includes('h1')) return 'H1 HEADING';
    return 'CONTENT';
  };

  const getCharLimit = (issueType: string): number => {
    if (issueType.includes('title')) return 60;
    if (issueType.includes('description')) return 160;
    if (issueType.includes('h1')) return 70;
    if (issueType === 'url_slug') return 60;
    return 200;
  };

  const getExistingContent = (): string => {
    if (!existingPageData || !selectedIssue) return '';
    if (selectedIssue.issue_type.includes('title')) return existingPageData.title || '';
    if (selectedIssue.issue_type.includes('description')) return existingPageData.metaDescription || '';
    if (selectedIssue.issue_type.includes('h1')) return existingPageData.h1 || '';
    return '';
  };

  const handleStartTask = (task: SEOTask) => {
    // Find the related issue for this task
    const relatedIssue = issues.find(issue =>
      issue.title === task.title || issue.id === task.related_issue_id
    );

    if (relatedIssue) {
      handleFixNowClick(relatedIssue);
    } else {
      toast.warning('Related issue not found. It may have been resolved or dismissed.');
    }
  };

  const handleDismissTask = async (taskId: string) => {
    if (!activeEnv) return;

    try {
      // Simply remove from UI - tasks are auto-generated from issues
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Failed to dismiss task:', error);
    }
  };

  const handleLoadFromSitemap = async () => {
    if (!activeEnv) return;

    try {
      setLoading(true);
      // Load user-configured sitemaps from Settings (API first, localStorage fallback)
      let sitemaps: string[] = [];
      try {
        const smResp = await fetch(`/api/sitemaps?environmentId=${activeEnv.id}`, { credentials: 'include' });
        if (smResp.ok) {
          const smData = await smResp.json();
          sitemaps = smData.sitemaps || [];
        }
      } catch (e) { /* ignore */ }
      if (sitemaps.length === 0) {
        const stored = localStorage.getItem(`sitemaps_${activeEnv.id}`);
        if (stored) sitemaps = JSON.parse(stored);
      }
      if (sitemaps.length === 0) {
        toast.warning('No sitemaps configured. Add your sitemap URLs in Settings first.');
        setLoading(false);
        return;
      }

      const allUrls: string[] = [];

      for (const sitemapUrl of sitemaps) {
        const response = await fetch(`/api/seo/sitemap?url=${encodeURIComponent(sitemapUrl)}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          allUrls.push(...data.urls);
        }
      }

      if (allUrls.length > 0) {
        // Limit to 500 URLs
        const urlsToAdd = allUrls.slice(0, 500);
        setAuditUrls(urlsToAdd.join('\n'));
        toast.success(`Loaded ${urlsToAdd.length} URLs from sitemaps${allUrls.length > 500 ? ' (limited to 500)' : ''}`);
      } else {
        toast.warning('No URLs found in sitemaps');
      }
    } catch (error) {
      console.error('Failed to load sitemap:', error);
      toast.error('Failed to load sitemaps. Please enter URLs manually.');
    } finally {
      setLoading(false);
    }
  };

  const handleRunAudit = async (runInBackground = false) => {
    if (!activeEnv || !auditUrls.trim()) return;

    // Strip anything after whitespace (e.g. sitemap timestamps appended to URLs)
    const urls = auditUrls.split('\n')
      .map(u => u.trim().split(/\s+/)[0])
      .filter(u => u && u.startsWith('http'));

    if (urls.length === 0) {
      toast.warning('Please enter at least one valid URL (must start with http:// or https://)');
      return;
    }

    if (urls.length > 500) {
      toast.warning('Maximum 500 URLs per audit. Please reduce the number of URLs.');
      return;
    }

    setAuditing(true);
    setAuditProgress({ current: 0, total: urls.length });

    if (runInBackground) {
      setBackgroundAudit(true);
      setShowAuditPanel(false);
    }

    try {
      // Process in batches of 20 for better performance.
      // All batches share one auditId so they merge into a single history row.
      const batchSize = 20;
      const auditId = Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      let processed = 0;

      for (let i = 0; i < urls.length; i += batchSize) {
        const batch = urls.slice(i, i + batchSize);

        const response = await fetch('/api/seo/monitor', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            environmentId: activeEnv.id,
            urls: batch,
            auditId
          })
        });

        if (!response.ok) {
          console.error(`Batch ${i / batchSize + 1} failed`);
        }

        processed += batch.length;
        setAuditProgress({ current: processed, total: urls.length });

        // Small delay between batches to avoid overwhelming the server
        if (i + batchSize < urls.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      await loadMonitoringData();
      setAuditUrls('');
      setBackgroundAudit(false);

      toast.success(`Audit complete! ${urls.length} URLs processed`);

    } catch (error) {
      console.error('Audit error:', error);
      toast.error('Audit failed. Please try again.');
      setBackgroundAudit(false);
    } finally {
      setAuditing(false);
      setAuditProgress({ current: 0, total: 0 });
    }
  };

  if (!activeEnv) return <EmptyState />;

  const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

  const searchLower = searchQuery.toLowerCase();

  const filteredIssues = issues.filter(issue => {
    if (selectedCategory !== 'all' && issue.category !== selectedCategory) return false;
    if (selectedSeverity !== 'all' && issue.severity !== selectedSeverity) return false;
    if (dateFilter !== 'all') {
      const now = Math.floor(Date.now() / 1000);
      const cutoff = dateFilter === '24h' ? now - 86400
        : dateFilter === '7d' ? now - 604800
        : dateFilter === '30d' ? now - 2592000
        : 0;
      if (issue.detected_at < cutoff) return false;
    }
    if (searchQuery && !(
      issue.title.toLowerCase().includes(searchLower) ||
      issue.url.toLowerCase().includes(searchLower) ||
      issue.description.toLowerCase().includes(searchLower) ||
      issue.issue_type.toLowerCase().includes(searchLower)
    )) return false;
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'severity': return severityOrder[a.severity] - severityOrder[b.severity];
      case 'date_newest': return b.detected_at - a.detected_at;
      case 'date_oldest': return a.detected_at - b.detected_at;
      case 'url': return a.url.localeCompare(b.url);
      case 'category': return a.category.localeCompare(b.category);
      default: return 0;
    }
  });

  const filteredIgnoredIssues = ignoredIssues.filter(issue => {
    if (!searchQuery) return true;
    return (
      issue.title.toLowerCase().includes(searchLower) ||
      issue.url.toLowerCase().includes(searchLower) ||
      issue.description.toLowerCase().includes(searchLower) ||
      (issue.ignore_reason || '').toLowerCase().includes(searchLower)
    );
  });

  const filteredDismissedIssues = dismissedIssues.filter(issue => {
    if (!searchQuery) return true;
    return (
      issue.title.toLowerCase().includes(searchLower) ||
      issue.url.toLowerCase().includes(searchLower) ||
      issue.description.toLowerCase().includes(searchLower)
    );
  });

  const filteredRecurringIssues = recurringIssues.filter(issue => {
    if (!searchQuery) return true;
    return (
      issue.title.toLowerCase().includes(searchLower) ||
      issue.url.toLowerCase().includes(searchLower) ||
      issue.description.toLowerCase().includes(searchLower)
    );
  });

  const toggleSelectIssue = (id: string) => {
    setSelectedIssueIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    let visibleIds: string[];
    if (issueTab === 'active') visibleIds = filteredIssues.map(i => i.id);
    else if (issueTab === 'ignored') visibleIds = filteredIgnoredIssues.map(i => i.id);
    else if (issueTab === 'dismissed') visibleIds = filteredDismissedIssues.map(i => i.id);
    else visibleIds = filteredRecurringIssues.map(i => i.id);
    setSelectedIssueIds(new Set(visibleIds));
  };

  const deselectAll = () => {
    setSelectedIssueIds(new Set());
  };

  const handleBulkIgnore = () => {
    if (selectedIssueIds.size === 0) return;
    setBulkIgnoreMode(true);
    setIgnoreReason('');
    setShowIgnoreModal(true);
  };

  const handleBulkDismiss = async () => {
    if (!activeEnv || selectedIssueIds.size === 0) return;

    const idsToDismiss = new Set(selectedIssueIds);
    const count = idsToDismiss.size;
    const now = Math.floor(Date.now() / 1000);

    const fromActive = issues.filter(i => idsToDismiss.has(i.id));
    const fromRecurring = recurringIssues.filter(i => idsToDismiss.has(i.id));
    const allToDismiss = [...fromActive, ...fromRecurring];
    const dismissedItems = allToDismiss.map(i => ({ ...i, status: 'dismissed', ignored_at: now }));

    // Persist keys to localStorage (normalized)
    const newDismissKeys = allToDismiss.map(i => normalizeKey(i.url, i.issue_type));
    const storedDismissed: string[] = JSON.parse(localStorage.getItem(dismissedStorageKey()) || '[]');
    localStorage.setItem(dismissedStorageKey(), JSON.stringify([...new Set([...storedDismissed, ...newDismissKeys])]));

    setDismissedIssues(prev => {
      const existing = new Set(prev.map(i => `${i.url}::${i.issue_type}`));
      const newItems = dismissedItems.filter(i => !existing.has(`${i.url}::${i.issue_type}`));
      return [...newItems, ...prev];
    });
    setIssues(prev => prev.filter(i => !idsToDismiss.has(i.id)));
    setRecurringIssues(prev => prev.filter(i => !idsToDismiss.has(i.id)));
    setSelectedIssueIds(new Set());
    toast.success(`${count} issues dismissed.`);

    try {
      for (const issueId of idsToDismiss) {
        await fetch('/api/seo/monitor', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ environmentId: activeEnv.id, issueId, action: 'dismiss' })
        });
      }
    } catch (error) {
      console.error('Bulk dismiss failed:', error);
      toast.warning('Dismissed locally but some server updates may have failed.');
    }
  };

  const handleBulkIgnoreConfirm = async () => {
    if (!activeEnv || !ignoreReason.trim() || selectedIssueIds.size === 0) return;

    const idsToIgnore = new Set(selectedIssueIds);
    const reason = ignoreReason.trim();
    const count = idsToIgnore.size;

    // Optimistic update first — collect from both active and recurring
    const now = Math.floor(Date.now() / 1000);
    const fromActive = issues.filter(i => idsToIgnore.has(i.id));
    const fromRecurring = recurringIssues.filter(i => idsToIgnore.has(i.id));
    const allToIgnore = [...fromActive, ...fromRecurring];
    const ignoredItems = allToIgnore
      .map(i => ({ ...i, status: 'ignored', ignore_reason: reason, ignored_at: now }));

    // Persist keys to localStorage (normalized)
    const newIgnoreKeys = allToIgnore.map(i => normalizeKey(i.url, i.issue_type));
    const storedIgnored: string[] = JSON.parse(localStorage.getItem(ignoredStorageKey()) || '[]');
    localStorage.setItem(ignoredStorageKey(), JSON.stringify([...new Set([...storedIgnored, ...newIgnoreKeys])]));

    setIgnoredIssues(prev => {
      const existing = new Set(prev.map(i => `${i.url}::${i.issue_type}`));
      const newItems = ignoredItems.filter(i => !existing.has(`${i.url}::${i.issue_type}`));
      return [...newItems, ...prev];
    });
    setIssues(prev => prev.filter(i => !idsToIgnore.has(i.id)));
    setRecurringIssues(prev => prev.filter(i => !idsToIgnore.has(i.id)));
    setSelectedIssueIds(new Set());
    setShowIgnoreModal(false);
    setBulkIgnoreMode(false);
    setIgnoreReason('');
    toast.info(`Moving ${count} issues to Ignored in the background...`);

    // API calls in background
    try {
      for (const issueId of idsToIgnore) {
        await fetch('/api/seo/monitor', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            environmentId: activeEnv.id,
            issueId,
            action: 'ignore',
            reason
          })
        });
      }
      toast.success(`${count} issues successfully ignored.`);
    } catch (error) {
      console.error('Bulk ignore failed:', error);
      toast.warning(`${count} issues moved locally but some server updates may have failed.`);
    }
  };

  const handleBulkReactivate = async () => {
    if (!activeEnv || selectedIssueIds.size === 0) return;

    const idsToReactivate = new Set(selectedIssueIds);
    const reactivated = ignoredIssues
      .filter(i => idsToReactivate.has(i.id))
      .map(i => ({ ...i, status: 'open', ignore_reason: undefined, ignored_at: undefined }));

    // Optimistic update first
    setIssues(prev => [...reactivated, ...prev]);
    setIgnoredIssues(prev => prev.filter(i => !idsToReactivate.has(i.id)));
    setSelectedIssueIds(new Set());
    toast.success(`${reactivated.length} issues moved to Active.`);

    try {
      for (const issueId of idsToReactivate) {
        await fetch('/api/seo/monitor', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            environmentId: activeEnv.id,
            issueId,
            action: 'reactivate'
          })
        });
      }
    } catch (error) {
      console.error('Bulk reactivate failed:', error);
      toast.warning('Moved locally but some server updates may have failed.');
    }
  };

  const handleRemoveDuplicates = async () => {
    if (!activeEnv) return;
    if (!confirm('Remove duplicate issues? Only exact duplicates (matching URL, type, severity, title, description, recommendation, and status) will be removed.')) return;

    try {
      const response = await fetch('/api/seo/monitor', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environmentId: activeEnv.id,
          action: 'deduplicate'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.removed > 0) {
          toast.success(`Removed ${data.removed} duplicate issues.`);
          await loadMonitoringData();
        } else {
          toast.info('No duplicates found.');
        }
      } else {
        toast.error('Failed to remove duplicates.');
      }
    } catch (error) {
      console.error('Dedup failed:', error);
      toast.error('Failed to remove duplicates.');
    }
  };

  const formatDuration = (seconds: number): string => {
    if (!seconds || seconds <= 0) return '—';
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  };

  const csvCell = (v: string | number) => {
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const fmtDate = (ts: number) => {
    const d = new Date(ts * 1000);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${m}/${day}/${y}`;
  };

  const fmtTime = (ts: number) => {
    const d = new Date(ts * 1000);
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${min}`;
  };

  const buildCSV = (headers: string[], rows: (string | number)[][]) =>
    [headers, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n');

  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const ignoredTotal = ignoredIssues.length;
    const ignoredCrit  = ignoredIssues.filter(i => i.severity === 'critical').length;
    const ignoredHigh  = ignoredIssues.filter(i => i.severity === 'high').length;
    const ignoredMed   = ignoredIssues.filter(i => i.severity === 'medium').length;
    const ignoredLow   = ignoredIssues.filter(i => i.severity === 'low').length;

    const headers = ['Date & Time', 'Type', 'Duration', 'Pages Audited', 'Active Issues', 'Critical', 'High', 'Medium', 'Low', 'New Pages'];
    const rows = auditHistory.map(e => [
      `${fmtDate(e.started_at)} ${fmtTime(e.started_at)}`,
      e.trigger_type,
      formatDuration(e.completed_at - e.started_at),
      e.pages_audited,
      Math.max(0, e.total_issues - ignoredTotal),
      Math.max(0, e.critical_issues - ignoredCrit),
      Math.max(0, e.high_issues - ignoredHigh),
      Math.max(0, e.medium_issues - ignoredMed),
      Math.max(0, e.low_issues - ignoredLow),
      e.new_pages,
    ]);
    downloadCSV(buildCSV(headers, rows), `site-monitor-audit-history-${fmtDate(Math.floor(Date.now()/1000))}.csv`);
    toast.success('Audit history exported as CSV.');
  };

  const handleExportIgnoredCSV = () => {
    const headers = ['URL', 'Issue Type', 'Severity', 'Title', 'Ignore Reason', 'Date Ignored'];
    const rows = ignoredIssues.map(i => [
      i.url,
      i.issue_type,
      i.severity,
      i.title,
      i.ignore_reason || '',
      i.ignored_at ? `${fmtDate(i.ignored_at)} ${fmtTime(i.ignored_at)}` : '',
    ]);
    downloadCSV(buildCSV(headers, rows), `site-monitor-ignored-issues-${fmtDate(Math.floor(Date.now()/1000))}.csv`);
    toast.success('Ignored issues exported as CSV.');
  };

  const handleExportFixedCSV = () => {
    const headers = ['URL', 'Issue Type', 'Severity', 'Title', 'Date Detected', 'Date Fixed'];
    const rows = dismissedIssues.map(i => [
      i.url,
      i.issue_type,
      i.severity,
      i.title,
      i.detected_at ? `${fmtDate(i.detected_at)} ${fmtTime(i.detected_at)}` : '',
      i.ignored_at ? `${fmtDate(i.ignored_at)} ${fmtTime(i.ignored_at)}` : '',
    ]);
    downloadCSV(buildCSV(headers, rows), `site-monitor-fixed-issues-${fmtDate(Math.floor(Date.now()/1000))}.csv`);
    toast.success('Fixed issues exported as CSV.');
  };

  const buildHTML = (title: string, headers: string[], rows: (string | number)[][]) => {
    const ths = headers.map(h => `<th>${h}</th>`).join('');
    const trs = rows.map(r =>
      `<tr>${r.map(c => `<td>${String(c).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</td>`).join('')}</tr>`
    ).join('\n');
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title><style>
body{font-family:system-ui,sans-serif;padding:24px;color:#1e293b}
h1{font-size:18px;font-weight:900;margin-bottom:16px}
table{border-collapse:collapse;width:100%;font-size:13px}
th{background:#003B71;color:#fff;padding:8px 12px;text-align:left;white-space:nowrap}
td{padding:7px 12px;border-bottom:1px solid #e2e8f0;white-space:nowrap}
tr:nth-child(even) td{background:#f8fafc}
tr:hover td{background:#f1f5f9}
</style></head><body><h1>${title}</h1><table><thead><tr>${ths}</tr></thead><tbody>\n${trs}\n</tbody></table></body></html>`;
  };

  const downloadHTML = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportHTML = () => {
    const ignoredTotal = ignoredIssues.length;
    const ignoredCrit  = ignoredIssues.filter(i => i.severity === 'critical').length;
    const ignoredHigh  = ignoredIssues.filter(i => i.severity === 'high').length;
    const ignoredMed   = ignoredIssues.filter(i => i.severity === 'medium').length;
    const ignoredLow   = ignoredIssues.filter(i => i.severity === 'low').length;
    const headers = ['Date & Time', 'Type', 'Duration', 'Pages Audited', 'Active Issues', 'Critical', 'High', 'Medium', 'Low', 'New Pages'];
    const rows = auditHistory.map(e => [
      `${fmtDate(e.started_at)} ${fmtTime(e.started_at)}`,
      e.trigger_type,
      formatDuration(e.completed_at - e.started_at),
      e.pages_audited,
      Math.max(0, e.total_issues - ignoredTotal),
      Math.max(0, e.critical_issues - ignoredCrit),
      Math.max(0, e.high_issues - ignoredHigh),
      Math.max(0, e.medium_issues - ignoredMed),
      Math.max(0, e.low_issues - ignoredLow),
      e.new_pages,
    ]);
    downloadHTML(buildHTML('Audit History — Site Monitor', headers, rows), `site-monitor-audit-history-${fmtDate(Math.floor(Date.now()/1000))}.html`);
    toast.success('Audit history exported as HTML.');
  };

  const handleExportIgnoredHTML = () => {
    const headers = ['URL', 'Issue Type', 'Severity', 'Title', 'Ignore Reason', 'Date Ignored'];
    const rows = ignoredIssues.map(i => [
      i.url, i.issue_type, i.severity, i.title,
      i.ignore_reason || '',
      i.ignored_at ? `${fmtDate(i.ignored_at)} ${fmtTime(i.ignored_at)}` : '',
    ]);
    downloadHTML(buildHTML('Ignored Issues — Site Monitor', headers, rows), `site-monitor-ignored-issues-${fmtDate(Math.floor(Date.now()/1000))}.html`);
    toast.success('Ignored issues exported as HTML.');
  };

  const handleExportFixedHTML = () => {
    const headers = ['URL', 'Issue Type', 'Severity', 'Title', 'Date Detected', 'Date Fixed'];
    const rows = dismissedIssues.map(i => [
      i.url, i.issue_type, i.severity, i.title,
      i.detected_at ? `${fmtDate(i.detected_at)} ${fmtTime(i.detected_at)}` : '',
      i.ignored_at  ? `${fmtDate(i.ignored_at)}  ${fmtTime(i.ignored_at)}`  : '',
    ]);
    downloadHTML(buildHTML('Fixed Issues — Site Monitor', headers, rows), `site-monitor-fixed-issues-${fmtDate(Math.floor(Date.now()/1000))}.html`);
    toast.success('Fixed issues exported as HTML.');
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-700 bg-red-50 border-red-200';
      case 'high': return 'text-orange-700 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-700 bg-blue-50 border-blue-200';
      default: return 'text-slate-700 bg-slate-50 border-slate-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-700 bg-red-50';
      case 'high': return 'text-orange-700 bg-orange-50';
      case 'medium': return 'text-yellow-700 bg-yellow-50';
      case 'low': return 'text-blue-700 bg-blue-50';
      default: return 'text-slate-700 bg-slate-50';
    }
  };

  const issueSummary = issues.reduce((acc: any, issue) => {
    acc[issue.severity] = (acc[issue.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="border-b border-slate-200 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-4xl font-black text-slate-900 tracking-tight">SEO Monitor</h2>
            <p className="text-slate-500 mt-2 text-md flex items-center gap-2">
              <Activity size={16} className="text-green-500 animate-pulse" />
              24/7 Real-Time SEO Monitoring & Alerts
            </p>
            {monitoring?.lastAuditAt && (
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Clock size={12} />
                Last audit: {new Date(monitoring.lastAuditAt * 1000).toLocaleString()}
                {' '}({auditHistory[0]?.trigger_type === 'auto' ? 'auto' : 'manual'})
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAuditPanel(!showAuditPanel)}
              className="px-4 py-2.5 bg-[#003B71] text-white rounded-xl font-bold text-sm hover:scale-105 transition-all shadow-md flex items-center gap-2"
            >
              <Target size={16} />
              Run Audit
            </button>
            <button
              onClick={() => { setShowMetaGenPanel(!showMetaGenPanel); setShowAuditPanel(false); }}
              className="px-4 py-2.5 bg-[#FFCD00] text-[#003B71] rounded-xl font-bold text-sm hover:scale-105 transition-all shadow-md flex items-center gap-2"
            >
              <Zap size={16} />
              New Page Meta
            </button>
            <button
              onClick={loadMonitoringData}
              disabled={loading}
              className="px-4 py-2.5 bg-white border-2 border-slate-200 text-[#003B71] rounded-xl font-bold text-sm hover:scale-105 transition-all shadow-md flex items-center gap-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Quick Audit Panel */}
      {showAuditPanel && (
        <div className="bg-white rounded-3xl border-2 border-[#FFCD00] shadow-lg p-6">
          <h3 className="font-black text-lg text-slate-900 mb-4">Run SEO Audit</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-slate-600 uppercase">
                  URLs to Audit (one per line, max 500)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleLoadFromSitemap}
                    className="text-[10px] font-bold text-[#003B71] hover:text-[#FFCD00] transition-colors px-2 py-1 bg-slate-100 rounded-md"
                  >
                    📄 Load from Sitemap
                  </button>
                  <span className="text-[10px] text-slate-500 font-medium">
                    {auditUrls.split('\n').filter(u => u.trim()).length} URLs entered
                  </span>
                </div>
              </div>
              <textarea
                value={auditUrls}
                onChange={(e) => setAuditUrls(e.target.value)}
                placeholder="https://example.com/&#10;https://example.com/research&#10;https://example.com/omega&#10;&#10;Paste up to 100 URLs (one per line)..."
                className="w-full h-40 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono resize-none focus:ring-2 focus:ring-[#FFCD00] outline-none"
              />
            </div>

            {auditing && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-blue-900">Processing URLs...</span>
                  <span className="text-xs font-bold text-blue-700">
                    {auditProgress.current} / {auditProgress.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(auditProgress.current / auditProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => handleRunAudit(false)}
                disabled={auditing || !auditUrls.trim()}
                className="px-6 py-3 bg-[#003B71] text-white rounded-xl font-bold text-sm hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {auditing ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Auditing...
                  </>
                ) : (
                  <>
                    <Zap size={16} />
                    Start Audit
                  </>
                )}
              </button>
              <button
                onClick={() => handleRunAudit(true)}
                disabled={auditing || !auditUrls.trim()}
                className="px-6 py-3 bg-[#FFCD00] text-[#003B71] rounded-xl font-bold text-sm hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Activity size={16} />
                Run in Background
              </button>
              <button
                onClick={() => setShowAuditPanel(false)}
                disabled={auditing}
                className="px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold text-sm hover:bg-slate-200 transition-all disabled:opacity-50"
              >
                {auditing ? 'Minimize' : 'Cancel'}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 italic">
              💡 Tip: Use "Run in Background" to continue working while the audit processes. You'll get a notification when complete.
            </p>
          </div>
        </div>
      )}

      {/* New Page Meta Generator Panel */}
      {showMetaGenPanel && (
        <div className="bg-white rounded-3xl border-2 border-[#FFCD00] shadow-lg p-6 animate-in fade-in duration-200">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-black text-lg text-slate-900">Generate Meta Tags for New Page</h3>
              <p className="text-xs text-slate-500 mt-0.5">For pages not yet published — paste your content and get optimized meta tags</p>
            </div>
            <button onClick={() => setShowMetaGenPanel(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Inputs */}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">
                  Generate
                </label>
                <select
                  value={metaGenType}
                  onChange={e => setMetaGenType(e.target.value as any)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-[#FFCD00] outline-none"
                >
                  <option value="missing_meta_description">Meta Description</option>
                  <option value="missing_meta_title">Page Title (Meta Title)</option>
                  <option value="missing_h1">H1 Heading</option>
                  <option value="url_slug">URL Slug</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">
                  Page Title / Topic <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={metaGenTitle}
                  onChange={e => setMetaGenTitle(e.target.value)}
                  placeholder="e.g. Omega Laser Facility Overview"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#FFCD00] outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">
                  Page Content / Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={metaGenContent}
                  onChange={e => setMetaGenContent(e.target.value)}
                  placeholder="Paste your page copy, key topics, or a brief description of what this page covers..."
                  className="w-full h-32 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-[#FFCD00] outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">
                  Target URL <span className="text-slate-400 font-normal">(optional — helps match your site's URL style)</span>
                </label>
                <input
                  type="text"
                  value={metaGenUrl}
                  onChange={e => setMetaGenUrl(e.target.value)}
                  placeholder="https://example.com/your-page-slug"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono focus:ring-2 focus:ring-[#FFCD00] outline-none"
                />
              </div>

              <button
                onClick={handleGenerateNewPageMeta}
                disabled={metaGenGenerating || (!metaGenTitle.trim() && !metaGenContent.trim())}
                className="w-full px-6 py-3 bg-[#003B71] text-white rounded-xl font-bold text-sm hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {metaGenGenerating ? (
                  <><RefreshCw size={16} className="animate-spin" /> Generating...</>
                ) : (
                  <><Zap size={16} /> Generate</>
                )}
              </button>
            </div>

            {/* Result */}
            <div className="flex flex-col">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2 block">
                ✨ Generated Result
              </label>
              {metaGenGenerating ? (
                <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 rounded-xl border border-slate-200 py-12">
                  <RefreshCw className="animate-spin text-[#003B71] mb-3" size={32} />
                  <p className="text-sm font-bold text-slate-700">Generating...</p>
                  <p className="text-xs text-slate-400 mt-1">Analyzing your content</p>
                </div>
              ) : metaGenResult ? (
                <div className="flex-1 flex flex-col gap-3">
                  <div className="flex-1 bg-white rounded-xl border-2 border-[#FFCD00] p-5">
                    {metaGenType === 'url_slug' ? (
                      <p className="text-base text-slate-900 font-mono">/{metaGenResult.replace(/^\/|\/$/g, '')}/</p>
                    ) : (
                      <p className="text-base leading-relaxed text-slate-900 font-medium">{metaGenResult}</p>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <span className={`text-xs font-bold ${
                        metaGenResult.length <= getCharLimit(metaGenType) ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {metaGenResult.length} / {getCharLimit(metaGenType)} chars
                      </span>
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle2 size={12} /> Optimized
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { navigator.clipboard.writeText(metaGenResult); toast.success('Copied to clipboard!'); }}
                      className="flex-1 px-4 py-2.5 bg-[#003B71] text-white rounded-xl font-bold text-sm hover:scale-105 transition-all"
                    >
                      📋 Copy
                    </button>
                    <button
                      onClick={handleGenerateNewPageMeta}
                      disabled={metaGenGenerating}
                      className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-bold text-sm hover:bg-slate-200 transition-all"
                    >
                      🔄 Regenerate
                    </button>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs font-bold text-blue-900 mb-1">📍 Where to add this in your page builder</p>
                    {metaGenType === 'missing_meta_description' && (
                      <p className="text-xs text-blue-800">Open the page → SEO Settings → Meta Description field → Paste and save.</p>
                    )}
                    {metaGenType === 'missing_meta_title' && (
                      <p className="text-xs text-blue-800">Open the page → SEO Settings → Page Title / SEO Title field → Paste and save.</p>
                    )}
                    {metaGenType === 'missing_h1' && (
                      <p className="text-xs text-blue-800">Add a Heading block at the top of your page content → Set style to H1 → Paste and save.</p>
                    )}
                    {metaGenType === 'url_slug' && (
                      <p className="text-xs text-blue-800">Update the URL slug in your CMS (WordPress: Edit page → Permalink field). Set up a 301 redirect from the old URL first.</p>
                    )}
                  </div>
                  {metaGenSlug && metaGenType !== 'url_slug' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 size={14} className="text-amber-700" />
                        <h4 className="font-bold text-amber-900 text-xs">Suggested SEO URL Slug</h4>
                      </div>
                      <div className="bg-white rounded-lg p-2.5 font-mono text-sm text-amber-900 border border-amber-200">
                        /{metaGenSlug}/
                      </div>
                      <p className="text-[10px] text-amber-700 mt-1.5">
                        Shorter, keyword-rich URLs rank better. Only change if you set up a redirect from the old URL.
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`/${metaGenSlug}/`);
                          toast.success('Slug copied to clipboard!');
                        }}
                        className="mt-2 px-3 py-1.5 bg-amber-200 hover:bg-amber-300 text-amber-900 rounded-lg text-xs font-bold transition-colors"
                      >
                        Copy Slug
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 rounded-xl border border-dashed border-slate-300 py-12 text-center px-6">
                  <Zap size={32} className="text-slate-300 mb-3" />
                  <p className="text-sm font-bold text-slate-500">Your generated content will appear here</p>
                  <p className="text-xs text-slate-400 mt-1">Fill in the title or content and click Generate</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Background Audit Indicator */}
      {backgroundAudit && !showAuditPanel && (
        <div className="fixed bottom-6 right-6 bg-[#003B71] text-white rounded-2xl shadow-2xl p-4 z-50 animate-in slide-in-from-bottom-2">
          <div className="flex items-center gap-3">
            <RefreshCw size={20} className="animate-spin text-[#FFCD00]" />
            <div>
              <p className="font-bold text-sm">Audit Running in Background</p>
              <p className="text-xs text-white/80">
                {auditProgress.current} / {auditProgress.total} URLs processed
              </p>
            </div>
            <button
              onClick={() => setShowAuditPanel(true)}
              className="ml-4 px-3 py-1.5 bg-[#FFCD00] text-[#003B71] rounded-lg text-xs font-bold hover:scale-105 transition-all"
            >
              Show
            </button>
          </div>
          <div className="w-full bg-white/20 rounded-full h-1.5 mt-3">
            <div
              className="bg-[#FFCD00] h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(auditProgress.current / auditProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Monitoring Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Eye size={24} className="opacity-80" />
            <span className="text-sm font-bold opacity-80">MONITORED</span>
          </div>
          <p className="text-4xl font-black">{monitoring?.pagesMonitored || 0}</p>
          <p className="text-sm opacity-80 mt-1">Pages Tracked</p>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle size={24} className="opacity-80" />
            <span className="text-sm font-bold opacity-80">ISSUES</span>
          </div>
          <p className="text-4xl font-black">{monitoring?.totalIssues || 0}</p>
          <p className="text-sm opacity-80 mt-1">
            {issueSummary.critical || 0} Critical • {issueSummary.high || 0} High
          </p>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Clock size={24} className="opacity-80" />
            <span className="text-sm font-bold opacity-80">CHANGES</span>
          </div>
          <p className="text-4xl font-black">{monitoring?.recentChanges || 0}</p>
          <p className="text-sm opacity-80 mt-1">Last 24 Hours</p>
        </div>

        <button
          onClick={() => setShowFixedPanel(true)}
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg text-left hover:scale-[1.02] transition-transform"
        >
          <div className="flex items-center justify-between mb-2">
            <CheckCircle2 size={24} className="opacity-80" />
            <span className="text-sm font-bold opacity-80">FIXED</span>
          </div>
          <p className="text-4xl font-black">{dismissedIssues.length}</p>
          <p className="text-sm opacity-80 mt-1">Issues Resolved</p>
        </button>
      </div>

      {/* Score Rings — Digital Certainty Index */}
      {/* Show when we have local audit scores OR Siteimprove scores, or when Siteimprove is configured (for pull button) */}
      {(scores || siScores || siConfigured) && (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-black text-lg text-slate-800 flex items-center gap-2">
                <Shield size={18} className="text-blue-600" />
                Digital Certainty Index
                {siScores && (
                  <span className="ml-2 px-2.5 py-0.5 text-[10px] font-bold bg-indigo-100 text-indigo-700 rounded-full uppercase tracking-wider">
                    Siteimprove Live
                  </span>
                )}
                {!siScores && scores && (
                  <span className="ml-2 px-2.5 py-0.5 text-[10px] font-bold bg-slate-100 text-slate-500 rounded-full uppercase tracking-wider">
                    Audit Estimated
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                {siScores ? 'Live scores from Siteimprove' : 'Site-wide quality scores based on your latest audit'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {!previousScores && scores && !siScores && (
                <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full uppercase tracking-wider">Run another audit to see trends</span>
              )}
              {siConfigured && (
                <button
                  onClick={fetchSiteimproveData}
                  disabled={siLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {siLoading ? (
                    <><RefreshCw size={14} className="animate-spin" /> Pulling...</>
                  ) : (
                    <><RefreshCw size={14} /> {siScores ? 'Refresh Siteimprove' : 'Pull Siteimprove Scores'}</>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Score rings - prefer Siteimprove over local audit */}
          {(siScores || scores) && (
            <div className="flex items-center justify-center gap-12 flex-wrap">
              <ScoreRing
                score={(siScores || scores)!.overall}
                previousScore={!siScores ? previousScores?.overall : null}
                label="Overall"
                color={(siScores || scores)!.overall >= 80 ? '#16a34a' : (siScores || scores)!.overall >= 60 ? '#d97706' : '#dc2626'}
                size={160}
              />
              <div className="w-px h-24 bg-slate-200 hidden md:block" />
              <ScoreRing
                score={(siScores || scores)!.seo}
                previousScore={!siScores ? previousScores?.seo : null}
                label="SEO"
                color="#2563eb"
              />
              <ScoreRing
                score={(siScores || scores)!.qa}
                previousScore={!siScores ? previousScores?.qa : null}
                label="Quality Assurance"
                color="#7c3aed"
              />
              <ScoreRing
                score={(siScores || scores)!.accessibility}
                previousScore={!siScores ? previousScores?.accessibility : null}
                label="Accessibility"
                color="#0891b2"
              />
            </div>
          )}

          {/* When no scores yet but Siteimprove is configured, show prompt */}
          {!siScores && !scores && siConfigured && !siLoading && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Shield size={40} className="text-slate-300 mb-3" />
              <p className="text-sm font-bold text-slate-500">No scores yet</p>
              <p className="text-xs text-slate-400 mt-1">Pull live data from Siteimprove or run an audit to generate scores</p>
            </div>
          )}

          {/* Siteimprove sub-score breakdown */}
          {siSummary && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Siteimprove Score Breakdown</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* SEO Sub-scores */}
                {siSummary.seo && (
                  <div className="bg-blue-50 rounded-xl p-4">
                    <h5 className="text-xs font-bold text-blue-900 mb-3 flex items-center gap-1.5">
                      <Search size={12} /> SEO
                    </h5>
                    <div className="space-y-2">
                      {['content', 'technical', 'ux', 'mobile'].map(key => siSummary.seo[key] > 0 && (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-[11px] text-slate-600 capitalize">{key === 'ux' ? 'User Experience' : key}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${siSummary.seo[key]}%` }} />
                            </div>
                            <span className="text-[11px] font-bold text-blue-800 w-8 text-right">{siSummary.seo[key]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* QA Sub-scores */}
                {siSummary.qa && (
                  <div className="bg-purple-50 rounded-xl p-4">
                    <h5 className="text-xs font-bold text-purple-900 mb-3 flex items-center gap-1.5">
                      <Shield size={12} /> Quality Assurance
                    </h5>
                    <div className="space-y-2">
                      {['content', 'freshness', 'security', 'ux'].map(key => siSummary.qa[key] > 0 && (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-[11px] text-slate-600 capitalize">{key === 'ux' ? 'User Experience' : key}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-purple-100 rounded-full overflow-hidden">
                              <div className="h-full bg-purple-500 rounded-full" style={{ width: `${siSummary.qa[key]}%` }} />
                            </div>
                            <span className="text-[11px] font-bold text-purple-800 w-8 text-right">{siSummary.qa[key]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Accessibility Sub-scores */}
                {siSummary.accessibility && (
                  <div className="bg-cyan-50 rounded-xl p-4">
                    <h5 className="text-xs font-bold text-cyan-900 mb-3 flex items-center gap-1.5">
                      <Eye size={12} /> Accessibility
                    </h5>
                    <div className="space-y-2">
                      {[
                        { key: 'levelA', label: 'Level A' },
                        { key: 'levelAA', label: 'Level AA' },
                        { key: 'aria', label: 'WAI-ARIA' },
                        { key: 'bestPractices', label: 'Best Practices' }
                      ].map(({ key, label }) => siSummary.accessibility[key] > 0 && (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-[11px] text-slate-600">{label}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-cyan-100 rounded-full overflow-hidden">
                              <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${siSummary.accessibility[key]}%` }} />
                            </div>
                            <span className="text-[11px] font-bold text-cyan-800 w-8 text-right">{siSummary.accessibility[key]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {siSummary.totalPages > 0 && (
                <p className="text-[10px] text-slate-400 mt-3 text-center">{siSummary.totalPages} total pages indexed by Siteimprove</p>
              )}
            </div>
          )}

          {/* Siteimprove Issues */}
          {siIssues.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                Siteimprove Issues ({siIssues.length})
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {siIssues.map((issue, idx) => (
                  <div key={idx} className="flex items-start gap-3 px-4 py-3 bg-slate-50 rounded-xl">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      issue.severity === 'error' || issue.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      issue.severity === 'warning' || issue.severity === 'high' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {issue.category}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-800 truncate">{issue.issue}</p>
                      {issue.recommendation && (
                        <p className="text-[10px] text-slate-500 mt-0.5 truncate">{issue.recommendation}</p>
                      )}
                    </div>
                    {issue.affectedPages > 0 && (
                      <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap">{issue.affectedPages} pages</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Issues Section with Tabs */}
      <div id="issues-section" className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Section collapse header */}
        <div className="flex items-center justify-between px-8 py-4 bg-slate-50/60 border-b border-slate-100">
          <button
            onClick={() => toggleSection('issues')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <span className="font-black text-slate-800 flex items-center gap-2">
              <AlertTriangle size={16} className="text-[#003B71]" /> Issues
              <span className="ml-1 text-xs font-bold text-slate-400">({issues.length} active)</span>
            </span>
            {sectionsOpen.issues ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          <button
            onClick={handleRemoveDuplicates}
            className="px-3 py-1.5 text-[10px] font-bold text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 hover:text-slate-700 transition-all flex items-center gap-1.5"
            title="Remove exact duplicate issues from the database"
          >
            <Trash2 size={12} />
            Remove Duplicates
          </button>
        </div>
        {sectionsOpen.issues && <>
        {/* Tab Bar */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => { setIssueTab('active'); setSelectedIssueIds(new Set()); }}
            className={`flex-1 min-w-[120px] px-6 py-4 text-sm font-black transition-colors flex items-center justify-center gap-2 ${
              issueTab === 'active'
                ? 'text-[#003B71] border-b-3 border-[#003B71] bg-white'
                : 'text-slate-400 hover:text-slate-600 bg-slate-50/50'
            }`}
          >
            <AlertTriangle size={16} />
            Active Issues
            {issues.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-red-500 text-white rounded-full text-[10px] font-black">
                {issues.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setIssueTab('dismissed'); setSelectedIssueIds(new Set()); }}
            className={`flex-1 min-w-[120px] px-6 py-4 text-sm font-black transition-colors flex items-center justify-center gap-2 ${
              issueTab === 'dismissed'
                ? 'text-[#003B71] border-b-3 border-[#003B71] bg-white'
                : 'text-slate-400 hover:text-slate-600 bg-slate-50/50'
            }`}
          >
            <MinusCircle size={16} />
            Dismissed
            {dismissedIssues.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-orange-400 text-white rounded-full text-[10px] font-black">
                {dismissedIssues.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setIssueTab('recurring'); setSelectedIssueIds(new Set()); }}
            className={`flex-1 min-w-[120px] px-6 py-4 text-sm font-black transition-colors flex items-center justify-center gap-2 ${
              issueTab === 'recurring'
                ? 'text-[#003B71] border-b-3 border-[#003B71] bg-white'
                : 'text-slate-400 hover:text-slate-600 bg-slate-50/50'
            }`}
          >
            <RotateCcw size={16} />
            Recurring
            {recurringIssues.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-purple-500 text-white rounded-full text-[10px] font-black">
                {recurringIssues.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setIssueTab('ignored'); setSelectedIssueIds(new Set()); }}
            className={`flex-1 min-w-[120px] px-6 py-4 text-sm font-black transition-colors flex items-center justify-center gap-2 ${
              issueTab === 'ignored'
                ? 'text-[#003B71] border-b-3 border-[#003B71] bg-white'
                : 'text-slate-400 hover:text-slate-600 bg-slate-50/50'
            }`}
          >
            <EyeOff size={16} />
            Ignored
            {ignoredIssues.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-slate-400 text-white rounded-full text-[10px] font-black">
                {ignoredIssues.length}
              </span>
            )}
          </button>
        </div>

        {/* Active Issues Tab */}
        {issueTab === 'active' && (
          <>
            <div className="px-8 py-4 border-b border-slate-100 bg-slate-50/50">
              {/* Search bar */}
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search issues by title, URL, or description..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#003B71] outline-none"
                />
              </div>
              {/* Bulk actions bar */}
              {selectedIssueIds.size > 0 && issueTab === 'active' && (
                <div className="flex items-center gap-3 mb-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <span className="text-xs font-bold text-blue-900">{selectedIssueIds.size} selected</span>
                  <button
                    onClick={handleBulkDismiss}
                    className="px-3 py-1.5 bg-slate-600 text-white rounded-lg text-xs font-bold hover:scale-105 transition-all"
                  >
                    Dismiss Selected
                  </button>
                  <button
                    onClick={handleBulkIgnore}
                    className="px-3 py-1.5 bg-[#003B71] text-white rounded-lg text-xs font-bold hover:scale-105 transition-all"
                  >
                    Ignore Selected
                  </button>
                  <button
                    onClick={deselectAll}
                    className="px-3 py-1.5 bg-white text-slate-600 rounded-lg text-xs font-bold border border-slate-200 hover:bg-slate-50 transition-all"
                  >
                    Deselect All
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                >
                  <option value="all">All Categories</option>
                  <option value="metadata">Metadata</option>
                  <option value="technical">Technical</option>
                  <option value="content">Content</option>
                  <option value="links">Links</option>
                </select>
                <select
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value)}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                >
                  <option value="severity">Sort: Severity</option>
                  <option value="date_newest">Sort: Newest First</option>
                  <option value="date_oldest">Sort: Oldest First</option>
                  <option value="url">Sort: URL</option>
                  <option value="category">Sort: Category</option>
                </select>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                >
                  <option value="all">All Time</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                <button
                  onClick={selectedIssueIds.size === filteredIssues.length && filteredIssues.length > 0 ? deselectAll : selectAllVisible}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                  {selectedIssueIds.size === filteredIssues.length && filteredIssues.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
                <span className="px-3 py-1.5 text-xs text-slate-500 font-bold self-center">
                  {filteredIssues.length} issues
                </span>
              </div>
            </div>

            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {filteredIssues.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle2 size={48} className="mx-auto text-green-500 mb-4" />
                  <p className="text-lg font-bold text-slate-900">No Active Issues!</p>
                  <p className="text-sm text-slate-500 mt-2">All monitored pages are healthy.</p>
                </div>
              ) : (
                filteredIssues.map((issue) => (
                  <div key={issue.id} className={`p-6 hover:bg-slate-50/50 transition-colors ${selectedIssueIds.has(issue.id) ? 'bg-blue-50/50' : ''}`}>
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={selectedIssueIds.has(issue.id)}
                        onChange={() => toggleSelectIssue(issue.id)}
                        className="mt-1 w-4 h-4 rounded border-slate-300 text-[#003B71] focus:ring-[#003B71] cursor-pointer"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase border ${getSeverityColor(issue.severity)}`}>
                            {issue.severity}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 rounded-lg text-[10px] font-bold uppercase text-slate-600">
                            {issue.category}
                          </span>
                          {newPageUrls.has(issue.url) && (
                            <span className="px-2 py-1 bg-green-500 text-white rounded-lg text-[10px] font-black uppercase">
                              NEW PAGE
                            </span>
                          )}
                        </div>
                        <h4 className="font-bold text-slate-900 mb-1">{issue.title}</h4>
                        <p className="text-sm text-slate-600 mb-2">{issue.description}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <ExternalLink size={12} />
                          <a href={issue.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#003B71] underline">
                            {issue.url}
                          </a>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleFixNowClick(issue)}
                          className="px-4 py-2 bg-[#FFCD00] text-[#003B71] rounded-lg text-xs font-bold hover:scale-105 transition-all"
                        >
                          Fix Now
                        </button>
                        <button
                          onClick={() => handleDismissIssue(issue, 'active')}
                          className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-200 transition-all"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                      <p className="text-xs font-bold text-blue-900 mb-1">💡 Recommendation:</p>
                      <p className="text-xs text-blue-800">{issue.recommendation}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Ignored Issues Tab */}
        {issueTab === 'ignored' && (
          <>
            <div className="px-8 py-4 border-b border-slate-100 bg-slate-50/50">
              {/* Search bar */}
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search ignored issues by title, URL, or reason..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#003B71] outline-none"
                />
              </div>
              {/* Bulk actions bar */}
              {selectedIssueIds.size > 0 && (
                <div className="flex items-center gap-3 mb-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                  <span className="text-xs font-bold text-green-900">{selectedIssueIds.size} selected</span>
                  <button
                    onClick={handleBulkReactivate}
                    className="px-3 py-1.5 bg-[#003B71] text-white rounded-lg text-xs font-bold hover:scale-105 transition-all"
                  >
                    Re-activate Selected
                  </button>
                  <button
                    onClick={deselectAll}
                    className="px-3 py-1.5 bg-white text-slate-600 rounded-lg text-xs font-bold border border-slate-200 hover:bg-slate-50 transition-all"
                  >
                    Deselect All
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={selectedIssueIds.size === filteredIgnoredIssues.length && filteredIgnoredIssues.length > 0 ? deselectAll : selectAllVisible}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                  {selectedIssueIds.size === filteredIgnoredIssues.length && filteredIgnoredIssues.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-xs text-slate-500 font-bold">{filteredIgnoredIssues.length} ignored issues</span>
                {ignoredIssues.length > 0 && (
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={handleExportIgnoredCSV}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all"
                    >
                      <Download size={12} /> CSV
                    </button>
                    <button
                      onClick={handleExportIgnoredHTML}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all"
                    >
                      <Download size={12} /> HTML
                    </button>
                  </div>
                )}
              </div>
            </div>
          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {filteredIgnoredIssues.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle2 size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-lg font-bold text-slate-900">No Ignored Issues</p>
                <p className="text-sm text-slate-500 mt-2">Issues you ignore will appear here with your reason for ignoring them.</p>
              </div>
            ) : (
              filteredIgnoredIssues.map((issue) => (
                <div key={issue.id} className={`p-6 hover:bg-slate-50/50 transition-colors opacity-80 ${selectedIssueIds.has(issue.id) ? 'bg-green-50/50 opacity-100' : ''}`}>
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      checked={selectedIssueIds.has(issue.id)}
                      onChange={() => toggleSelectIssue(issue.id)}
                      className="mt-1 w-4 h-4 rounded border-slate-300 text-[#003B71] focus:ring-[#003B71] cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase border ${getSeverityColor(issue.severity)}`}>
                          {issue.severity}
                        </span>
                        <span className="px-2 py-1 bg-slate-100 rounded-lg text-[10px] font-bold uppercase text-slate-600">
                          {issue.category}
                        </span>
                        <span className="px-2 py-1 bg-slate-500 text-white rounded-lg text-[10px] font-black uppercase">
                          IGNORED
                        </span>
                      </div>
                      <h4 className="font-bold text-slate-900 mb-1">{issue.title}</h4>
                      <p className="text-sm text-slate-600 mb-2">{issue.description}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                        <ExternalLink size={12} />
                        <a href={issue.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#003B71] underline">
                          {issue.url}
                        </a>
                      </div>
                      {/* Ignore reason */}
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-xs font-bold text-amber-900 mb-1">Reason for ignoring:</p>
                        <p className="text-xs text-amber-800 italic">"{issue.ignore_reason || 'No reason provided'}"</p>
                        {issue.ignored_at && (
                          <p className="text-[10px] text-amber-600 mt-1">
                            Ignored on {new Date(issue.ignored_at * 1000).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() => handleReactivateIssue(issue)}
                        className="px-4 py-2 bg-[#003B71] text-white rounded-lg text-xs font-bold hover:scale-105 transition-all"
                      >
                        Move to Active
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          </>
        )}

        {/* Dismissed Issues Tab */}
        {issueTab === 'dismissed' && (
          <>
            <div className="px-8 py-4 border-b border-slate-100 bg-slate-50/50">
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search dismissed issues..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#003B71] outline-none"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-bold">{filteredDismissedIssues.length} dismissed issues</span>
                  <span className="text-xs text-slate-400">• Only cleared when you manually clear them</span>
                </div>
                {dismissedIssues.length > 0 && (
                  <button
                    onClick={handleClearDismissed}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-bold transition-all"
                  >
                    <Trash2 size={12} /> Clear All
                  </button>
                )}
              </div>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {filteredDismissedIssues.length === 0 ? (
                <div className="p-12 text-center">
                  <MinusCircle size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-lg font-bold text-slate-900">No Dismissed Issues</p>
                  <p className="text-sm text-slate-500 mt-2">Issues you dismiss will appear here. They may reappear on the next audit.</p>
                </div>
              ) : (
                filteredDismissedIssues.map((issue) => (
                  <div key={issue.id} className="p-6 hover:bg-slate-50/50 transition-colors opacity-80">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase border ${getSeverityColor(issue.severity)}`}>
                            {issue.severity}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 rounded-lg text-[10px] font-bold uppercase text-slate-600">
                            {issue.category}
                          </span>
                          <span className="px-2 py-1 bg-orange-400 text-white rounded-lg text-[10px] font-black uppercase">
                            DISMISSED
                          </span>
                        </div>
                        <h4 className="font-bold text-slate-900 mb-1">{issue.title}</h4>
                        <p className="text-sm text-slate-600 mb-2">{issue.description}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <ExternalLink size={12} />
                          <a href={issue.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#003B71] underline">
                            {issue.url}
                          </a>
                        </div>
                        {issue.ignored_at && (
                          <p className="text-[10px] text-slate-400 mt-1">
                            Dismissed on {new Date(issue.ignored_at * 1000).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <button
                          onClick={() => handleReactivateFromDismissed(issue)}
                          className="px-4 py-2 bg-[#003B71] text-white rounded-lg text-xs font-bold hover:scale-105 transition-all"
                        >
                          Re-activate
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Recurring Issues Tab */}
        {issueTab === 'recurring' && (
          <>
            <div className="px-8 py-4 border-b border-slate-100 bg-slate-50/50">
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search recurring issues..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#003B71] outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-bold">{filteredRecurringIssues.length} recurring issues</span>
                <span className="text-xs text-slate-400">• These issues have reappeared after being previously resolved</span>
              </div>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {filteredRecurringIssues.length === 0 ? (
                <div className="p-12 text-center">
                  <RotateCcw size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-lg font-bold text-slate-900">No Recurring Issues</p>
                  <p className="text-sm text-slate-500 mt-2">Issues that reappear after being resolved will show up here.</p>
                </div>
              ) : (
                filteredRecurringIssues.map((issue) => (
                  <div key={issue.id} className="p-6 hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase border ${getSeverityColor(issue.severity)}`}>
                            {issue.severity}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 rounded-lg text-[10px] font-bold uppercase text-slate-600">
                            {issue.category}
                          </span>
                          <span className="px-2 py-1 bg-purple-500 text-white rounded-lg text-[10px] font-black uppercase">
                            RECURRING
                          </span>
                        </div>
                        <h4 className="font-bold text-slate-900 mb-1">{issue.title}</h4>
                        <p className="text-sm text-slate-600 mb-2">{issue.description}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <ExternalLink size={12} />
                          <a href={issue.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#003B71] underline">
                            {issue.url}
                          </a>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleFixNowClick(issue)}
                          className="px-4 py-2 bg-[#FFCD00] text-[#003B71] rounded-lg text-xs font-bold hover:scale-105 transition-all"
                        >
                          Fix Now
                        </button>
                        <button
                          onClick={() => handleDismissIssue(issue, 'recurring')}
                          className="px-4 py-2 bg-green-50 text-green-700 rounded-lg text-xs font-bold hover:bg-green-100 transition-all border border-green-200"
                        >
                          Dismiss Again
                        </button>
                        <button
                          onClick={() => handleOpenIgnoreModal(issue)}
                          className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-200 transition-all"
                        >
                          Ignore
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-purple-50 border border-purple-100 rounded-xl">
                      <p className="text-xs font-bold text-purple-900 mb-1">💡 Recommendation:</p>
                      <p className="text-xs text-purple-800">{issue.recommendation}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
        </>}
      </div>

      {/* Recent Changes */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          <button
            onClick={() => toggleSection('changes')}
            className="w-full flex items-center justify-between px-6 py-4 bg-slate-50/60 hover:bg-slate-100/60 transition-colors"
          >
            <h3 className="font-black text-lg text-slate-900 flex items-center gap-2">
              <Clock size={16} className="text-amber-500" /> Recent Changes
              <span className="text-xs font-bold text-slate-400">({changes.length})</span>
            </h3>
            {sectionsOpen.changes ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {sectionsOpen.changes && <>
          <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
            {changes.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                No recent changes detected
              </div>
            ) : (
              changes.slice(0, 10).map((change) => (
                <div key={change.id} className="p-4 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock size={12} className="text-slate-400" />
                    <span className="text-[10px] text-slate-500">
                      {new Date(change.detected_at * 1000).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs font-bold text-slate-900 mb-1">
                    {change.field_name.replace(/_/g, ' ').toUpperCase()} Changed
                  </p>
                  <div className="text-[10px] text-slate-600 space-y-1">
                    <div className="flex items-start gap-2">
                      <span className="font-bold">From:</span>
                      <span className="flex-1 italic">{change.old_value || '(empty)'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="font-bold">To:</span>
                      <span className="flex-1 italic">{change.new_value || '(empty)'}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          </>}
      </div>

      {/* Audit History */}
      {auditHistory.length > 0 && (() => {
        // Ignored breakdown (current state — exact for latest audit, approximate for older)
        const ignoredTotal = ignoredIssues.length;
        const ignoredCrit  = ignoredIssues.filter(i => i.severity === 'critical').length;
        const ignoredHigh  = ignoredIssues.filter(i => i.severity === 'high').length;
        const ignoredMed   = ignoredIssues.filter(i => i.severity === 'medium').length;
        const ignoredLow   = ignoredIssues.filter(i => i.severity === 'low').length;

        // Filter
        const now = Math.floor(Date.now() / 1000);
        const filteredHistory = auditHistory.filter(e => {
          if (historyTypeFilter !== 'all' && e.trigger_type !== historyTypeFilter) return false;
          if (historyDateFilter === '30d') return e.started_at >= now - 2592000;
          if (historyDateFilter === '90d') return e.started_at >= now - 7776000;
          if (historyDateFilter === '1y')  return e.started_at >= now - 31536000;
          return true;
        });

        const totalPages = Math.ceil(filteredHistory.length / HISTORY_PAGE_SIZE);
        const paginated = filteredHistory.slice(
          historyPage * HISTORY_PAGE_SIZE,
          (historyPage + 1) * HISTORY_PAGE_SIZE
        );

        // Summary stats across filtered set
        const totalAudited = filteredHistory.reduce((s, e) => s + e.pages_audited, 0);
        const avgIssues = filteredHistory.length
          ? Math.round(filteredHistory.reduce((s, e) => s + e.total_issues, 0) / filteredHistory.length)
          : 0;
        const totalNewPages = filteredHistory.reduce((s, e) => s + e.new_pages, 0);

        return (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Collapse toggle */}
            <button
              onClick={() => toggleSection('history')}
              className="w-full flex items-center justify-between px-8 py-4 bg-slate-50/60 hover:bg-slate-100/60 transition-colors border-b border-slate-100"
            >
              <h3 className="font-black text-lg text-slate-900 flex items-center gap-2">
                <Calendar size={16} className="text-[#003B71]" /> Audit History
                <span className="text-xs font-bold text-slate-400">({auditHistory.length})</span>
              </h3>
              {sectionsOpen.history ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {sectionsOpen.history && <>
            {/* Header */}
            <div className="px-8 py-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-black text-xl text-slate-900 flex items-center gap-2">
                    <Calendar size={20} className="text-[#003B71]" />
                    Audit History
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {filteredHistory.length} of {auditHistory.length} audits
                    {historyDateFilter !== 'all' || historyTypeFilter !== 'all' ? ' (filtered)' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {ignoredTotal > 0 && (
                    <button
                      onClick={() => {
                        setIssueTab('ignored');
                        setSelectedIssueIds(new Set());
                        document.getElementById('issues-section')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-200 transition-all"
                    >
                      <EyeOff size={14} />
                      Ignored ({ignoredTotal})
                    </button>
                  )}
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-[#003B71] text-white rounded-xl text-xs font-bold hover:scale-105 transition-all"
                  >
                    <Download size={14} />
                    Export CSV
                  </button>
                  <button
                    onClick={handleExportHTML}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold hover:scale-105 transition-all"
                  >
                    <Download size={14} />
                    Export HTML
                  </button>
                </div>
              </div>

              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-[#003B71]">{filteredHistory.length}</p>
                  <p className="text-[10px] text-slate-500 font-bold uppercase mt-0.5">Total Audits</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-slate-800">{avgIssues}</p>
                  <p className="text-[10px] text-slate-500 font-bold uppercase mt-0.5">Avg Issues / Audit</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-green-700">{totalNewPages}</p>
                  <p className="text-[10px] text-slate-500 font-bold uppercase mt-0.5">New Pages Found</p>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <select
                  value={historyDateFilter}
                  onChange={e => { setHistoryDateFilter(e.target.value); setHistoryPage(0); }}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                >
                  <option value="all">All Time</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="1y">Last Year</option>
                </select>
                <select
                  value={historyTypeFilter}
                  onChange={e => { setHistoryTypeFilter(e.target.value); setHistoryPage(0); }}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                >
                  <option value="all">All Types</option>
                  <option value="auto">Auto Only</option>
                  <option value="manual">Manual Only</option>
                </select>
                <span className="self-center text-xs text-slate-400 font-medium">
                  Showing {paginated.length} of {filteredHistory.length}
                </span>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500 tracking-wider border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-3">Date & Time</th>
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3">Duration</th>
                    <th className="px-6 py-3">Pages</th>
                    <th className="px-6 py-3">Total Issues</th>
                    <th className="px-6 py-3">Trend</th>
                    <th className="px-4 py-3 text-red-500">Crit</th>
                    <th className="px-4 py-3 text-orange-500">High</th>
                    <th className="px-4 py-3 text-yellow-500">Med</th>
                    <th className="px-4 py-3 text-blue-500">Low</th>
                    <th className="px-6 py-3">New Pages</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginated.map((entry, idx) => {
                    // Find the previous entry in the full sorted list for trend delta
                    const globalIdx = filteredHistory.indexOf(entry);
                    const prev = filteredHistory[globalIdx + 1];
                    const activeTotal = Math.max(0, entry.total_issues - ignoredTotal);
                    const prevActive  = prev ? Math.max(0, prev.total_issues - ignoredTotal) : null;
                    const delta = prevActive !== null ? activeTotal - prevActive : null;

                    return (
                      <tr key={entry.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-3">
                          <div className="font-bold text-slate-900 text-xs">
                            {new Date(entry.started_at * 1000).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </div>
                          <div className="text-[10px] text-slate-400">
                            {new Date(entry.started_at * 1000).toLocaleTimeString('en-US', {
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${
                            entry.trigger_type === 'auto'
                              ? 'bg-blue-50 text-blue-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {entry.trigger_type}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-xs text-slate-500 font-mono">
                          {formatDuration(entry.completed_at - entry.started_at)}
                        </td>
                        <td className="px-6 py-3 font-bold text-slate-700 text-sm">{entry.pages_audited}</td>
                        <td className="px-6 py-3">
                          <span className="font-black text-slate-900 text-sm">{activeTotal}</span>
                          {ignoredTotal > 0 && (
                            <span className="ml-1.5 text-[10px] text-slate-400 font-medium">{ignoredTotal} ignored</span>
                          )}
                        </td>
                        <td className="px-6 py-3">
                          {delta === null ? (
                            <span className="text-slate-300 text-xs">—</span>
                          ) : delta > 0 ? (
                            <span className="flex items-center gap-1 text-red-600 text-xs font-bold">
                              <TrendingUp size={12} />+{delta}
                            </span>
                          ) : delta < 0 ? (
                            <span className="flex items-center gap-1 text-green-600 text-xs font-bold">
                              <TrendingDown size={12} />{delta}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">±0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => { const v = Math.max(0, entry.critical_issues - ignoredCrit); return v > 0 ? <span className="font-bold text-red-600 text-sm">{v}</span> : <span className="text-slate-300 text-xs">—</span>; })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => { const v = Math.max(0, entry.high_issues - ignoredHigh); return v > 0 ? <span className="font-bold text-orange-600 text-sm">{v}</span> : <span className="text-slate-300 text-xs">—</span>; })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => { const v = Math.max(0, entry.medium_issues - ignoredMed); return v > 0 ? <span className="font-bold text-yellow-600 text-sm">{v}</span> : <span className="text-slate-300 text-xs">—</span>; })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => { const v = Math.max(0, entry.low_issues - ignoredLow); return v > 0 ? <span className="font-bold text-blue-600 text-sm">{v}</span> : <span className="text-slate-300 text-xs">—</span>; })()}
                        </td>
                        <td className="px-6 py-3">
                          {entry.new_pages > 0
                            ? <span className="px-2 py-1 bg-green-50 text-green-700 rounded-md text-[10px] font-bold">+{entry.new_pages}</span>
                            : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <span className="text-xs text-slate-500 font-medium">
                  Page {historyPage + 1} of {totalPages} &nbsp;·&nbsp; {filteredHistory.length} total audits
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setHistoryPage(p => Math.max(0, p - 1))}
                    disabled={historyPage === 0}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    const page = totalPages <= 7 ? i
                      : historyPage < 4 ? i
                      : historyPage > totalPages - 5 ? totalPages - 7 + i
                      : historyPage - 3 + i;
                    return (
                      <button
                        key={page}
                        onClick={() => setHistoryPage(page)}
                        className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${
                          page === historyPage
                            ? 'bg-[#003B71] text-white'
                            : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {page + 1}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setHistoryPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={historyPage >= totalPages - 1}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>}
          </div>
        );
      })()}

      {/* Fix Now Modal */}
      {showFixModal && selectedIssue && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col animate-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#003B71] to-[#002952] text-white px-8 py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase border ${
                      selectedIssue.severity === 'critical' ? 'bg-red-500/20 border-red-400' :
                      selectedIssue.severity === 'high' ? 'bg-orange-500/20 border-orange-400' :
                      'bg-yellow-500/20 border-yellow-400'
                    }`}>
                      {selectedIssue.severity}
                    </span>
                    <span className="px-2 py-1 bg-white/10 rounded-lg text-[10px] font-bold uppercase">
                      {selectedIssue.category}
                    </span>
                  </div>
                  <h2 className="text-2xl font-black mb-2">{selectedIssue.title}</h2>
                  <p className="text-sm text-white/80 mb-3">{selectedIssue.description}</p>
                  <a
                    href={selectedIssue.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs text-[#FFCD00] hover:underline"
                  >
                    <ExternalLink size={12} />
                    {selectedIssue.url}
                  </a>
                </div>
                <button
                  onClick={() => setShowFixModal(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8 overflow-y-auto flex-1 min-h-0">
              {optimizing ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <RefreshCw className="animate-spin text-[#003B71] mb-4" size={48} />
                  <p className="text-lg font-bold text-slate-900">Analyzing your page...</p>
                  <p className="text-sm text-slate-500 mt-2">Generating optimized {getFieldLabel(selectedIssue.issue_type).toLowerCase()}</p>
                </div>
              ) : generatedFix ? (
                <>
                  {/* Current vs Optimized */}
                  {getExistingContent() && (
                    <div className="mb-6 pb-6 border-b border-slate-200">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2 block">
                        Current {getFieldLabel(selectedIssue.issue_type)}
                      </label>
                      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 italic">
                        "{getExistingContent()}"
                      </div>
                      <p className="text-xs text-slate-400 mt-2">{getExistingContent().length} characters</p>
                    </div>
                  )}

                  {/* Optimized Content */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                        {optimizedContent ? '✨ AI-Optimized ' : ''}
                        {getFieldLabel(selectedIssue.issue_type)}
                      </label>
                      <span className={`text-sm font-bold ${
                        generatedFix.length <= getCharLimit(selectedIssue.issue_type)
                          ? 'text-green-600'
                          : 'text-orange-600'
                      }`}>
                        {generatedFix.length} / {getCharLimit(selectedIssue.issue_type)}
                      </span>
                    </div>

                    <div className="bg-white rounded-xl p-6 border-2 border-slate-200">
                      <p className="text-lg leading-relaxed text-slate-900">
                        {generatedFix}
                      </p>
                    </div>

                    {optimizedContent && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-green-600">
                        <CheckCircle2 size={14} />
                        <span>AI-optimized based on your actual page content</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={handleCopyText}
                      className="w-full px-6 py-3 bg-[#003B71] hover:bg-[#002952] text-white rounded-xl font-bold text-sm transition-colors"
                    >
                      📋 COPY TO CLIPBOARD
                    </button>

                    {existingPageData && (
                      <button
                        onClick={handleOptimize}
                        disabled={optimizing}
                        className="w-full px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-sm transition-colors"
                      >
                        {optimizing ? '⏳ Optimizing...' : optimizedContent ? '🔄 OPTIMIZE AGAIN' : '✨ OPTIMIZE WITH AI'}
                      </button>
                    )}
                  </div>

                  {/* URL Slug Suggestion */}
                  {suggestedSlug && (
                    <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 size={14} className="text-amber-700" />
                        <h4 className="font-bold text-amber-900 text-sm">Suggested SEO URL Slug</h4>
                      </div>
                      <div className="bg-white rounded-lg p-3 font-mono text-sm text-amber-900 border border-amber-200">
                        /{suggestedSlug}/
                      </div>
                      <p className="text-[10px] text-amber-700 mt-2">
                        Shorter, keyword-rich URLs rank better. Only change if you set up a redirect from the old URL.
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`/${suggestedSlug}/`);
                          toast.success('Slug copied to clipboard!');
                        }}
                        className="mt-2 px-3 py-1.5 bg-amber-200 hover:bg-amber-300 text-amber-900 rounded-lg text-xs font-bold transition-colors"
                      >
                        Copy Slug
                      </button>
                    </div>
                  )}

                  {/* Instructions */}
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h4 className="font-bold text-blue-900 text-sm mb-2">📍 Where to Add in Your Page Builder</h4>
                    <ul className="text-xs text-blue-800 space-y-1.5 list-disc list-inside">
                      {selectedIssue.issue_type.includes('title') && (
                        <>
                          <li>Open your page in the page builder</li>
                          <li>Find "SEO Settings" or "Page Settings"</li>
                          <li>Look for the "Page Title" or "SEO Title" field</li>
                        </>
                      )}
                      {selectedIssue.issue_type.includes('description') && (
                        <>
                          <li>Open your page in the page builder</li>
                          <li>Find "SEO Settings" or "Page Settings"</li>
                          <li>Look for the "Meta Description" field</li>
                        </>
                      )}
                      {selectedIssue.issue_type.includes('h1') && (
                        <>
                          <li>Open your page in the page builder</li>
                          <li>Add a heading block at the top of your content</li>
                          <li>Set it to "Heading 1" or "H1" style</li>
                        </>
                      )}
                      <li>Paste the text above</li>
                      <li>Save and publish your page</li>
                    </ul>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-slate-500">Loading...</p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="bg-slate-50 px-8 py-5 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => { if (selectedIssue) handleOpenIgnoreModal(selectedIssue); }}
                className="flex-1 px-6 py-3 bg-white hover:bg-slate-100 text-slate-700 rounded-xl font-bold border border-slate-200 transition-colors"
              >
                Ignore Issue
              </button>
              <button
                onClick={() => setShowFixModal(false)}
                className="flex-1 px-6 py-3 bg-[#FFCD00] hover:bg-[#FFD700] text-[#003B71] rounded-xl font-bold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Issues Panel */}
      {showFixedPanel && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col animate-in slide-in-from-bottom duration-200">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-5 rounded-t-3xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black">Issues Fixed</h2>
                <p className="text-purple-200 text-sm mt-0.5">{dismissedIssues.length} resolved issues</p>
              </div>
              <div className="flex items-center gap-2">
                {dismissedIssues.length > 0 && (
                  <>
                    <button
                      onClick={handleExportFixedCSV}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-xl text-xs font-bold transition-all"
                    >
                      <Download size={13} /> CSV
                    </button>
                    <button
                      onClick={handleExportFixedHTML}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-xl text-xs font-bold transition-all"
                    >
                      <Download size={13} /> HTML
                    </button>
                    <button
                      onClick={handleClearDismissed}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/30 hover:bg-red-500/50 rounded-xl text-xs font-bold transition-all"
                    >
                      <Trash2 size={13} /> Clear All
                    </button>
                  </>
                )}
                <button onClick={() => setShowFixedPanel(false)} className="p-1.5 hover:bg-white/20 rounded-xl transition-colors">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {dismissedIssues.length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  <CheckCircle2 size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-bold text-slate-500">No fixed issues yet</p>
                  <p className="text-sm mt-1">Issues you dismiss will appear here</p>
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500 tracking-wider border-b border-slate-100 sticky top-0">
                    <tr>
                      <th className="px-5 py-3">Page</th>
                      <th className="px-4 py-3">Issue</th>
                      <th className="px-4 py-3">Severity</th>
                      <th className="px-4 py-3">Detected</th>
                      <th className="px-4 py-3">Fixed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dismissedIssues.map(issue => (
                      <tr key={issue.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-5 py-3 max-w-[200px]">
                          <p className="text-xs font-mono text-slate-600 truncate">{issue.url.replace(/^https?:\/\/[^/]+/, '')}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs font-bold text-slate-800">{issue.title}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            issue.severity === 'critical' ? 'bg-red-100 text-red-700' :
                            issue.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                            issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>{issue.severity}</span>
                        </td>
                        <td className="px-4 py-3 text-[11px] text-slate-400">
                          {issue.detected_at ? fmtDate(issue.detected_at) : '—'}
                        </td>
                        <td className="px-4 py-3 text-[11px] text-slate-400">
                          {issue.ignored_at ? fmtDate(issue.ignored_at) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ignore Reason Modal (single + bulk) */}
      {showIgnoreModal && (ignoreIssue || bulkIgnoreMode) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full animate-in zoom-in duration-200">
            <div className="bg-gradient-to-r from-slate-600 to-slate-700 text-white px-8 py-6 rounded-t-3xl">
              <h2 className="text-xl font-black mb-1">
                {bulkIgnoreMode ? `Ignore ${selectedIssueIds.size} Issues` : 'Ignore Issue'}
              </h2>
              {!bulkIgnoreMode && ignoreIssue && (
                <>
                  <p className="text-sm text-white/80">{ignoreIssue.title}</p>
                  <p className="text-xs text-white/60 mt-1 truncate">{ignoreIssue.url}</p>
                </>
              )}
              {bulkIgnoreMode && (
                <p className="text-sm text-white/80">
                  {selectedIssueIds.size} issues will be moved to the Ignored tab
                </p>
              )}
            </div>

            <div className="p-8">
              <p className="text-sm text-slate-600 mb-4">
                {bulkIgnoreMode ? 'These issues' : 'This issue'} will be moved to the <strong>Ignored</strong> tab and will <strong>not reappear</strong> on future audits. You can re-activate {bulkIgnoreMode ? 'them' : 'it'} later if needed.
              </p>

              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2 block">
                Reason for ignoring <span className="text-red-500">*</span>
              </label>
              <select
                value={ignoreReason}
                onChange={(e) => setIgnoreReason(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#003B71] outline-none appearance-none cursor-pointer"
                autoFocus
              >
                <option value="">Select a reason...</option>
                {IGNORE_REASONS.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={bulkIgnoreMode ? handleBulkIgnoreConfirm : handleIgnoreIssue}
                  disabled={!ignoreReason.trim()}
                  className="flex-1 px-6 py-3 bg-[#003B71] hover:bg-[#002952] text-white rounded-xl font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkIgnoreMode ? `Ignore ${selectedIssueIds.size} Issues` : 'Confirm Ignore'}
                </button>
                <button
                  onClick={() => { setShowIgnoreModal(false); setIgnoreIssue(null); setIgnoreReason(''); setBulkIgnoreMode(false); }}
                  className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOMonitor;
